import React, { useState, useEffect, useRef } from "react";
import {
  faFilter as faFilterSolid,
  faAngleRight,
  faAngleDown,
  faR,
  faFilterCircleXmark,
  faC,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SearchInput, {
  SearchInputHandle,
} from "../../../components/SearchInput";

import TooltipIcon from "../../../components/ToolTip";
import { useModal } from "../../../components/Modal";
import FilterBox from "./FilterBox";
import { useSelector, useDispatch } from "react-redux";
import {
  setQuery,
  setupQuery,
  setAxesWithAllSelected,
  selectCurrentTab,
} from "../../../redux/queryBuilderSlice";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  defaultFilterSelections,
  filterSelectionsType,
  TreeNode,
  LevelNode,
  Query,
  FilterBoxSelections,
} from "../../../interfaces/queryBuilderInterfaces";
import FilterBoxInfinite from "./FilterBoxInfinite";
import FilterBoxWithoutOptions from "./FilterBoxWithoutOptions";
import FilterLayout from "./FilterLayout";
import {
  convertFilterCriteriaToCondition,
  convertFilterCriteriaToFormula,
  convertFilterCriteriaToSelection,
  convertSelectionIntoFilter,
} from "../../../utils/util";
import TooltipWrapper from "../../../components/TooltipWrapper";

const updateQuery = (
  query: any,
  treeData: TreeNode,
  item: filterSelectionsType,
) => {
  const filterFieldName = treeData.uniquename
    ? treeData.uniquename
    : treeData.levels && treeData.levels.length > 0
      ? treeData.levels[0].uniquename
      : "";
  // Pick as per selected Time Span
  const axisRows = query?.axes["ROWS"] ?? [];
  const axisColumns = query?.axes["COLUMNS"] ?? [];

  let selectedFieldName = filterFieldName;
  if (treeData.levels && treeData.levels.length > 0) {
    selectedFieldName =
      treeData.levels.find((level: LevelNode) =>
        axisRows.includes(level.uniquename),
      )?.uniquename ?? "";
    if (!selectedFieldName) {
      selectedFieldName =
        treeData.levels.find((level: LevelNode) =>
          axisColumns.includes(level.uniquename),
        )?.uniquename ?? ""; 
    }
  }
  if (selectedFieldName === "" ) {
    if(!item.isForNamedSet) {
selectedFieldName = treeData.levels[0].uniquename;
    }
    // if no row/column selected in previous steps, pick first level
    
  }
  const enhancedFilterCriteria = query?.enhancedFilterCriteria ?? {};

  const updatedRows =
    item.applyTo === "Rows"
      ? axisRows.includes(selectedFieldName)
        ? item.isForNamedSet ? (item.selectedFilters.length > 0 ? axisRows : axisRows.filter((row: string) => row !== selectedFieldName)) : axisRows
        : item.isForNamedSet ? (item.selectedFilters.length > 0 ? [...axisRows,selectedFieldName] : axisRows) : [...axisRows, selectedFieldName]
      : axisRows.filter((row: string) => row !== selectedFieldName);

  const updatedColumns =
    item.applyTo === "Columns"
      ? axisColumns.includes(selectedFieldName)
        ? item.isForNamedSet ? (item.selectedFilters.length > 0 ? axisColumns : axisColumns.filter((col: string) => col !== selectedFieldName)) : axisColumns
        : item.isForNamedSet ? (item.selectedFilters.length > 0 ? [...axisColumns, selectedFieldName] : axisColumns) : [...axisColumns, selectedFieldName]
      : axisColumns.filter((row: string) => row !== selectedFieldName);
  let updatedEnhancedFilterCriteria = {
    ...enhancedFilterCriteria,
    [filterFieldName]: convertSelectionIntoFilter(item),
  };
  const isFilterSelectionEmpty =
    item.selectedFilters.length === 0 &&
    (!item.condition || item.condition.value.trim() === "") &&
    (!item.formula || item.formula.filter1Value.trim() === "");

  if (isFilterSelectionEmpty) {
    const existingCriteria = enhancedFilterCriteria[filterFieldName];
    const shouldRemoveNamedSet = item.isForNamedSet;

    const filteredCriteria = Array.isArray(existingCriteria)
      ? existingCriteria
          .map((criteriaGroup: any) => {
            const prunedCriteria = Array.isArray(criteriaGroup?.criteria)
              ? criteriaGroup.criteria
                  .map((criteriaItem: any) => {
                    const prunedValues = Array.isArray(criteriaItem?.values)
                      ? criteriaItem.values.filter(
                          (valueItem: { namedSet?: boolean }) =>
                            shouldRemoveNamedSet
                              ? !valueItem?.namedSet
                              : Boolean(valueItem?.namedSet),
                        )
                      : [];

                    return { ...criteriaItem, values: prunedValues };
                  })
                  .filter(
                    (criteriaItem: any) =>
                      Array.isArray(criteriaItem?.values) &&
                      criteriaItem.values.length > 0,
                  )
              : [];

            return { ...criteriaGroup, criteria: prunedCriteria };
          })
          .filter(
            (criteriaGroup: any) =>
              Array.isArray(criteriaGroup?.criteria) &&
              criteriaGroup.criteria.length > 0,
          )
      : [];

    if (filteredCriteria.length > 0) {
      updatedEnhancedFilterCriteria[filterFieldName] = filteredCriteria;
    } else {
      delete updatedEnhancedFilterCriteria[filterFieldName];
    }
  }

  const updatedCriteriaLength =
    updatedEnhancedFilterCriteria[filterFieldName]?.length;
  const existingCriteriaLength = enhancedFilterCriteria[filterFieldName]?.length;
  const shouldKeepAxesAsIs =
    !item.isForNamedSet &&
    ((updatedCriteriaLength === 0 && existingCriteriaLength === 0) ||
      (updatedCriteriaLength === undefined &&
        existingCriteriaLength === undefined));

  const shouldRemoveFromAxes =
    isFilterSelectionEmpty &&
    (!updatedEnhancedFilterCriteria[filterFieldName] ||
      updatedEnhancedFilterCriteria[filterFieldName].length === 0) &&
    !shouldKeepAxesAsIs;

  const finalRows = isFilterSelectionEmpty
    ? shouldRemoveFromAxes
      ? axisRows.filter((row: string) => row !== selectedFieldName)
      : shouldKeepAxesAsIs
        ? updatedRows
        : axisRows
    : updatedRows;

  const finalColumns = isFilterSelectionEmpty
    ? shouldRemoveFromAxes
      ? axisColumns.filter((column: string) => column !== selectedFieldName)
      : shouldKeepAxesAsIs
        ? updatedColumns
        : axisColumns
    : updatedColumns;

  const updated = {
    ...query,
    axes: { ROWS: finalRows, COLUMNS: finalColumns },
    enhancedFilterCriteria: updatedEnhancedFilterCriteria,
  };
  return updated;
};

const getSearchPriority = (text: string, searchValue: string) => {
  const normalizedText = text.toLowerCase();
  const normalizedSearchValue = searchValue.trim().toLowerCase();

  if (normalizedText.startsWith(normalizedSearchValue)) {
    return 0;
  }

  if (normalizedText.includes(normalizedSearchValue)) {
    return 1;
  }

  return 2;
};

const sortBySearchPriority = <T,>(
  items: T[],
  getText: (item: T) => string,
  searchValue: string,
) =>
  [...items].sort((firstItem, secondItem) => {
    const firstPriority = getSearchPriority(getText(firstItem), searchValue);
    const secondPriority = getSearchPriority(getText(secondItem), searchValue);

    if (firstPriority !== secondPriority) {
      return firstPriority - secondPriority;
    }

    return getText(firstItem).localeCompare(getText(secondItem));
  });

type PanelTab = "displayFields" | "namedSet";

const queryAttributeTabs: { id: PanelTab; label: string }[] = [
  { id: "displayFields", label: "Display Fields" },
  { id: "namedSet", label: "Named Set" },
];

export default function QueryAttributes({
  currentActiveTab,
  activePanelTab,
  setActivePanelTab
}: {
  currentActiveTab: number;
  activePanelTab: PanelTab;
  setActivePanelTab: (tab: PanelTab) => void;
}) {
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [localTreeData, setLocalTreeData] = useState<TreeNode[]>([]);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const currTabData = useSelector(selectCurrentTab);
  const metaData = useSelector((state: any) => state.meta);
  const [showSelectedAttributesOnly, setShowSelectedAttributesOnly] =
    useState(false);

  const meta = useSelector(
    (state: any) => state.meta.metaStates[currentActiveTab],
  );

  const search = async (val: string) => {
    if (val === "") {
      if (showSelectedAttributesOnly) {
        showSelectedAttributes(query);
      } else {
        setLocalTreeData(treeData);
      }
      return;
    }
    let updatedTreeData: TreeNode[] = [];
    const normalizedSearchValue = val.trim().toLowerCase();

    const rowData = showSelectedAttributesOnly
      ? selectedAttributesData(query)
      : treeData;

    rowData.forEach((item) => {
      const levelsToSearch =
        item.name === "Time Period"
          ? (item.levels ?? [])
          : sortBySearchPriority(
              item.levels ?? [],
              (level) => level.caption ?? "",
              normalizedSearchValue,
            );
      const checkItem = levelsToSearch.some(
        (level) =>
          level.caption &&
          level.caption.toLowerCase().includes(normalizedSearchValue),
      );
      const checkParents = item.name
        .toLowerCase()
        .includes(normalizedSearchValue);
      if (checkItem || checkParents) {
        if (checkParents && item.name == "Time Period") {
          updatedTreeData.push({ ...item });
        } else {
          const filterLevel = levelsToSearch.filter(
            (level) =>
              level.caption &&
              level.caption.toLowerCase().includes(normalizedSearchValue),
          );
          updatedTreeData.push({ ...item, levels: filterLevel });
        }
      }
    });
    updatedTreeData = updatedTreeData.filter(
      (child) => child?.levels?.length > 0,
    );
    const expandedNodes = updatedTreeData
      .filter((item) => item.name !== "Time Period")
      .map((item) => item.name);
    setExpandedNodes(expandedNodes);
    setLocalTreeData(updatedTreeData);
  };
  const [filterSelections, setFilterSelections] =
    useState<filterSelectionsType>(defaultFilterSelections);

  const toggleNode = (id: string) => {
    if (id === "Time Period") {
      //useModal(<FilterTimeBox treeData={treeData} />);
    } else {
      setExpandedNodes((prev) =>
        prev.includes(id)
          ? prev.filter((nodeId) => nodeId !== id)
          : [...prev, id],
      );
    }
  };

  const metadataGroup = useSelector(
    (state: any) => state.meta.metaStates[currentActiveTab].metadataGroup,
  );
  const dimensions = useSelector(
    (state: any) => state.meta.metaStates[currentActiveTab].dimensions,
  );

  useEffect(() => {
    // const { dimensions, metadataGroup } = meta;
    if (metadataGroup) {
      const treLevels: { [key: string]: any } = {};
      dimensions.forEach((dimentionsNode: { hierarchies: any }) => {
        const hierarchies = dimentionsNode?.hierarchies;
        if (hierarchies) {
          hierarchies.forEach((hierarchy: { levels: any }) => {
            const levels = hierarchy?.levels;
            if (levels) {
              levels.forEach((level: { uniqueName: string | number }) => {
                treLevels[level.uniqueName] = level;
              });
            }
          });
        }
      });
      const treeData = metadataGroup.map(
        (group: { name: string; levels: any }) => {
          return {
            name: group.name,
            caption: group.name,
            levels: group.levels.map((uniqueName: string) => {
              const treeLevelData = treLevels[uniqueName];
              return {
                uniquename: treeLevelData ? treeLevelData.uniqueName : "",
                name: treeLevelData ? treeLevelData.name : "",
                caption: treeLevelData ? treeLevelData.caption : "",
                levels: [],
                properties: treeLevelData ? treeLevelData.properties : {},
                memberCount: treeLevelData ? treeLevelData.memberCount : 0,
              };
            }),
          };
        },
      );
      setTreeData(treeData);
      setLocalTreeData(treeData);
    }
  }, [metadataGroup]);

  const selectedCube = useSelector(
    (state: any) => state.meta.metaStates[currentActiveTab].selectedCube,
  );

  const searchRef = useRef<SearchInputHandle>(null);

  const isNewQuery = useSelector((state: RootState) => currTabData.isNewQuery);

  useEffect(() => {
    if (isNewQuery) {
      if (searchRef.current) {
        searchRef.current.clear();
        search("");
        setExpandedNodes([]); // Collapse all nodes when a new query is created
      }
      setShowSelectedAttributesOnly(false); // Reset the showSelectedAttributesOnly state
    }
  }, [isNewQuery]);

  useEffect(() => {
    if (selectedCube && treeData && treeData.length > 0) {
      // clear search input when a new cube is selected
      if (searchRef.current) {
        searchRef.current.clear();

        search("");
        setExpandedNodes([]); // Collapse all nodes when a new cube is selected
      }
    }
  }, [selectedCube]);
  const query = useSelector((state: RootState) => currTabData.query);

  useEffect(() => {
    if (query && showSelectedAttributesOnly) {
      showSelectedAttributes(query);
    } else if (query && treeData && treeData.length > 0) {
      const val = searchRef.current?.getValue() || "";
      if (val !== "") {
        search(val);
      } else {
        setLocalTreeData(treeData);
      }
    }
  }, [query, showSelectedAttributesOnly, currentActiveTab, activePanelTab]);
  const selectedAttributesData = (query: any) => {

    const updatedTreeData: TreeNode[] = [];
    const selectedAttributes = new Set<string>();

    if(activePanelTab === "displayFields") {
      query?.axes["ROWS"].forEach((row: string) => {
      selectedAttributes.add(row);
    });
    query?.axes["COLUMNS"].forEach((row: string) => {
      selectedAttributes.add(row);
    });
    }
    
    query?.enhancedFilterCriteria &&
      Object.keys(query.enhancedFilterCriteria).forEach((row: string) => {
        // if(activePanelTab === "namedSet") {
          const criteriaList = query.enhancedFilterCriteria[row][0].criteria;
          // const selectedFilters: any[] = [];

          const selectedFilters: any[] = [];
          criteriaList.forEach((criteria: any) => {
            criteria.values.forEach(
              (items: { value: string; selType?: string; namedSet?: boolean }) => {
                selectedFilters.push({
                  name: items.value,
                  altCaption: items.value,
                  selType: items.selType,
                  isNamedSet: items.namedSet,
                });
              }
            );
          });
          const hasNamedSetFilters = selectedFilters.some((flr:any) => flr.isNamedSet);
          const hasDisplayFilters = selectedFilters.some((flr:any) => !flr.isNamedSet);
            if(hasNamedSetFilters && activePanelTab === "namedSet") {

              selectedAttributes.add(row);
            }
            if(hasDisplayFilters && activePanelTab === "displayFields") {
             
              selectedAttributes.add(row);
            }
            if(hasNamedSetFilters && activePanelTab === "displayFields" && !hasDisplayFilters) {
              selectedAttributes.delete(row);
            }
        
    
      });
    treeData.forEach((item) => {
      const checkItem = item.levels?.some((level) =>
        selectedAttributes.has(level.uniquename),
      );
      if (checkItem) {
        const existingItem = updatedTreeData.find(
          (existing) => existing.name === item.name,
        );
        const filterLevel = item.levels.filter((level) =>
          selectedAttributes.has(level.uniquename),
        );
        if (existingItem) {
          if (
            !(
              existingItem.levels &&
              existingItem.levels.some(
                (existingLevel) =>
                  existingLevel.uniquename === filterLevel[0].uniquename,
              )
            )
          ) {
            existingItem.levels.push(filterLevel[0]);
          }
        } else {
          updatedTreeData.push({ ...item, levels: filterLevel });
        }
      }
    });
    return updatedTreeData;
  };
  const showSelectedAttributes = async (query: Query) => {
    const updatedTreeData = await selectedAttributesData(query);
    const expandedNodes = updatedTreeData
      .filter((item) => item.name !== "Time Period")
      .map((item) => item.name);
    setExpandedNodes(expandedNodes);
    setLocalTreeData(updatedTreeData);
  };

  useEffect(() => {
    if (searchRef.current && treeData) {
      searchRef.current.clear();
      search("");
    }
    setExpandedNodes([]);
    setShowSelectedAttributesOnly(false);
  }, [currentActiveTab, treeData]);

  return (
    <>
      <div className="left-panel-qa">
        <div
          className="qa-tabs-container"
          role="tablist"
          aria-label="Query Attributes Tabs"
        >
          {queryAttributeTabs.map((tab) => (
            <div
              key={tab.id}
              role="tab"
              aria-selected={activePanelTab === tab.id}
              className={`qa-tab-item ${activePanelTab === tab.id ? "active" : ""}`}
              onClick={() => setActivePanelTab(tab.id)}
            >
              <span className="qa-tab-label">{tab.label}</span>
            </div>
          ))}
        </div>

        <div className="search-filter">
          <SearchInput
            callback={(val) => {
              search(val);
              if (val === "" && !showSelectedAttributesOnly) {
                setExpandedNodes([]);
              }
            }}
            debounce={300}
            placeholder="Search"
            ref={searchRef}
          />
          <span
            className="filter-icon-attr"
            onClick={() =>
              setShowSelectedAttributesOnly(!showSelectedAttributesOnly)
            }
          >
            <TooltipIcon
              icon={
                showSelectedAttributesOnly ? faFilterCircleXmark : faFilterSolid
              }
              message={
                showSelectedAttributesOnly ? "Show All" : "Show Selected"
              }
              position="left"
            />
          </span>
        </div>

        <div className="tree">
          <div className="tree-container">
            {localTreeData?.map((child) =>
              activePanelTab === "namedSet" &&
              child.name == "Time Period" ? null : (
                <Tree
                  key={child.name}
                  treeData={child}
                  activePanelTab={activePanelTab}
                  currentActiveTab={currentActiveTab}
                  expandedNodes={expandedNodes}
                  toggleNode={toggleNode}
                />
              ),
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export function Tree({
  treeData,
  expandedNodes,
  toggleNode,
  activePanelTab,
  currentActiveTab,
}: TreeProps) {
  const [isExpanded, setIsExpanded] = useState(
    expandedNodes.includes(treeData.name),
  );
  const hasMatchingChildInExpandedNodes = expandedNodes.includes(treeData.name);
  useEffect(() => {
    setIsExpanded(hasMatchingChildInExpandedNodes);
  }, [expandedNodes, hasMatchingChildInExpandedNodes]);

  const handleToggle = () => {
    toggleNode(treeData.name);
    if (treeData.name !== "Time Period") {
      setIsExpanded(!isExpanded);
    }
  };

  let children = treeData.levels ?? [];
  return (
    <div className="tree-items item">
      {children.length > 0 ? (
        <TreeNodeItem
          currentActiveTab={currentActiveTab}
          treeData={treeData}
          activePanelTab={activePanelTab}
          isExpanded={isExpanded}
          callBack={handleToggle}
        />
      ) : (
        <LevelNodeItem
          treeData={treeData}
          currentActiveTab={currentActiveTab}
          activePanelTab={activePanelTab}
        />
      )}
      {isExpanded &&
        children.map((child) => (
          <div className="child-Container" key={child.uniquename}>
            <Tree
              activePanelTab={activePanelTab}
              currentActiveTab={currentActiveTab}
              treeData={child}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
            />
          </div>
        ))}
    </div>
  );
}

export function TreeNodeItem({
  treeData,
  isExpanded,
  callBack,
  currentActiveTab,
  activePanelTab,
}: TreeNodeItemProps) {
  const categoryRef = useRef<HTMLDivElement | null>(null);
  if (treeData.name !== "Time Period") {
    return (
      <div
        id={treeData.name}
        data-name={treeData.name}
        className="parent-nav"
        onClick={callBack}
        ref={categoryRef}
      >
        <span className="expand-collapse-icon">
          <FontAwesomeIcon
            size="2xs"
            icon={isExpanded ? faAngleDown : faAngleRight}
          />
        </span>
        <span className="nav-label">{treeData.name}</span>
      </div>
    );
  } else {
    const [filterSelections, setFilterSelections] =
      useState<filterSelectionsType>(defaultFilterSelections);

    const queryBuilder = useSelector(selectCurrentTab);
    const [hasFilter, setHasFilter] = useState<boolean>(false);
    const dispatch: AppDispatch = useDispatch();
    // time priod filter
    const openFilterModal = useModal(
      <FilterLayout
        treeData={treeData}
        activePanelTab={activePanelTab}
        filterSelections={filterSelections}
        applyFilter={(item: filterSelectionsType, datatype: string) =>
          applyFilterHandler(item, datatype)
        }
      />,
    );

    useEffect(() => {
      if (queryBuilder.query) {
        const query = queryBuilder.query;
        const fieldName =
          treeData.levels && treeData.levels.length > 0
            ? (treeData.levels[0].uniquename?.toString() ?? "")
            : "";

        const axisRows = query?.axes["ROWS"] ?? [];
        const axisColumns = query?.axes["COLUMNS"] ?? [];
        const enhancedFilterCriteria = query?.enhancedFilterCriteria ?? {};
        if (enhancedFilterCriteria[fieldName]) {
          const filterData = enhancedFilterCriteria[fieldName][0].criteria[0];
          const selectedFilters = filterData.values.map(
            (items: {
              value: string;
              selType?: string;
              namedSet?: boolean;
            }) => {
              return {
                name: items.value,
                altCaption: items.value,
                selType: items.selType,
                isNamedSet: items.namedSet,
              };
            },
          );
          const exclude = !filterData.incExc;
          if (treeData.levels && treeData.levels.length > 0) {
            // Check if any level exists in axisRows or axisColumns
            const levelExistsInRows = treeData.levels.some((level: any) =>
              axisRows.includes(level.uniquename),
            );
            const levelExistsInColumns = treeData.levels.some((level: any) =>
              axisColumns.includes(level.uniquename),
            );
            const applyTo = levelExistsInRows
              ? "Rows"
              : levelExistsInColumns
                ? "Columns"
                : "Filters";
            setFilterSelections({
              selectedFilters,
              isForNamedSet: selectedFilters.some((sel: any) => sel.isNamedSet),
              exclude,
              applyTo,
            });
          } else {
            const applyTo = axisRows.includes(fieldName)
              ? "Rows"
              : axisColumns.includes(fieldName)
                ? "Columns"
                : "Filters";
            setFilterSelections({
              selectedFilters,
              isForNamedSet: selectedFilters.some((sel: any) => sel.isNamedSet),
              exclude,
              applyTo,
            });
          }
          setHasFilter(true);
        } else {
          const applyTo = axisRows.includes(fieldName)
            ? "Rows"
            : axisColumns.includes(fieldName)
              ? "Columns"
              : null;
          setFilterSelections({ ...defaultFilterSelections, applyTo });
        }
      }
    }, [queryBuilder.query, treeData]);

    const applyFilterHandler = (
      item: filterSelectionsType,
      datatype: string,
    ) => {
      setFilterSelections(item);
      const updatedQuery: any = updateQuery(queryBuilder.query, treeData, item);
         dispatch(
        setQuery({ query: updatedQuery, currentTabIndex: currentActiveTab }),
      );
      dispatch(setupQuery({ payload: updatedQuery, index: currentActiveTab }));
   
    };

    return (
      <div
        id={treeData.uniquename}
        data-name={treeData.uniquename}
        className="parent-nav"
        ref={categoryRef}
      >
        {filterSelections.applyTo !== "Filters" && (
          <div className="filter-icon-circle">
            <TooltipWrapper
              content={filterSelections.applyTo ?? ""}
              position="top"
            >
              <span style={{ lineHeight: 1, color: "#fff", fontSize: 12 }}>
                {filterSelections.applyTo === "Rows" ? "R" : "C"}
              </span>
            </TooltipWrapper>
          </div>
        )}
        <span className="time-nav-label nav-label">{`${treeData.name}`}</span>
        <span className="time-period-filter-icon" onClick={openFilterModal}>
          <TooltipIcon
            icon={hasFilter ? faFilterCircleXmark : faFilterSolid}
            message=""
            position="left"
          />
        </span>
      </div>
    );
  }
}

export function LevelNodeItem({
  treeData,
  currentActiveTab,
  activePanelTab,
}: LevelItemProps) {
  const [filterSelections, setFilterSelections] =
    useState<filterSelectionsType>(defaultFilterSelections);
  const [rowColumnFilterApplied, setRowColumnFilterApplied] = useState(false);
  const queryBuilder = useSelector(selectCurrentTab);
  const dispatch: AppDispatch = useDispatch();

    const selectedFilters = filterSelections.selectedFilters ?? [];
  const hasNamedSetFilters = selectedFilters.some((flr) => flr.isNamedSet);
  const hasDisplayFilters = selectedFilters.some((flr) => !flr.isNamedSet);
  const hasConditionOrFormula = Boolean(
    filterSelections?.condition || filterSelections?.formula,
  );
  const isFilterMatchForActivePanel =
    activePanelTab === "namedSet"
      ? hasNamedSetFilters
      : activePanelTab === "displayFields"
        ? hasDisplayFilters ||
          hasConditionOrFormula
        : false;

  const applyFilterHandler = (item: filterSelectionsType, datatype: string) => {
    const axesWithAllSelected = [...queryBuilder.axesWithAllSelected];
    setFilterSelections(item);
    if (item.isAllSelected) {
      if (!axesWithAllSelected.includes(treeData.uniquename)) {
        axesWithAllSelected.push(treeData.uniquename);
      }
    } else {
      if (axesWithAllSelected.includes(treeData.uniquename)) {
        const axesIndex = axesWithAllSelected.indexOf(treeData.uniquename);
        axesWithAllSelected.splice(axesIndex, 1);
      }
    }

    const updatedQuery: any = updateQuery(queryBuilder.query, treeData, item);

    checkHierarchyFilterExist(treeData, updatedQuery);
    
    dispatch(
      setQuery({ query: updatedQuery, currentTabIndex: currentActiveTab }),
    );
     dispatch(
      setAxesWithAllSelected({
        axes: axesWithAllSelected,
        currentTabIndex: currentActiveTab,
      }),
    );
    dispatch(setupQuery({ payload: updatedQuery, index: currentActiveTab }));
   

  };

  const metadataGroup = useSelector(
    (state: any) => state.meta.metaStates[currentActiveTab].properties,
  );

  const checkHierarchyFilterExist = (treeData: any, query: Query) => {
    let hierarchyFilterExist = false;
    Object.keys(metadataGroup.oneWayFilters).forEach((key) => {
      const oneWay = metadataGroup.oneWayFilters[key];
      let levelFound = false;
      for (var i = 0; i < oneWay.levels.length; i++) {
        if (oneWay.levels[i].levelUniqueName === treeData.uniquename) {
          levelFound = true;
        } else if (levelFound) {
          const childLevel = oneWay.levels[i].levelUniqueName;
          if (
            childLevel &&
            query.enhancedFilterCriteria[childLevel] &&
            query.enhancedFilterCriteria[childLevel].length > 0
          ) {
            // If child level has filter criteria, then hierarchy filter exists
            hierarchyFilterExist = true;
            break;
          }
        }
      }
      if (hierarchyFilterExist) {
        query.filterLevel = treeData.uniquename;
        return;
      }
    });
  };

  // non time period filter
  const openFilterModal = useModal(
    <FilterLayout
      treeData={treeData}
      activePanelTab={activePanelTab}
      filterSelections={filterSelections}
      applyFilter={(item: filterSelectionsType, datatype: string) =>
        applyFilterHandler(item, datatype)
      }
    />,
  );
  const filterClickHandler = () => {
    openFilterModal();
  };
  const labelClickHandler = () => {
    let filterSelectionData;

    if (filterSelections.applyTo && filterSelections.applyTo !== "Filters") {
      const filterExists = !(
        filterSelections.selectedFilters.length === 0 &&
        (!filterSelections.condition ||
          filterSelections.condition.value.trim() === "") &&
        (!filterSelections.formula ||
          filterSelections.formula.filter1Value.trim() === "")
      );
      filterSelectionData = filterExists
        ? { ...filterSelections, applyTo: "Filters" }
        : defaultFilterSelections;
    } else {
      filterSelectionData = {
        ...filterSelections,
        isForNamedSet: false,
        applyTo: "Rows",
      };
    }
    setFilterSelections(filterSelectionData);
    const updatedQuery: any = updateQuery(
      queryBuilder.query,
      treeData,
      filterSelectionData,
    );
    dispatch(setupQuery({ payload: updatedQuery, index: currentActiveTab }));
    dispatch(
      setQuery({ query: updatedQuery, currentTabIndex: currentActiveTab }),
    );
  };
  useEffect(() => {
    if (queryBuilder.query) {
      const query = queryBuilder.query;
      const fieldName = treeData.uniquename?.toString() ?? "";
      const axisRows = query?.axes["ROWS"] ?? [];
      const axisColumns = query?.axes["COLUMNS"] ?? [];
      const enhancedFilterCriteria = query?.enhancedFilterCriteria ?? {};
      if (enhancedFilterCriteria[fieldName]) {
        const selectionData =
          convertFilterCriteriaToSelection(enhancedFilterCriteria[fieldName]) ??
          defaultFilterSelections;
        const conditionData = convertFilterCriteriaToCondition(
          enhancedFilterCriteria[fieldName],
        );
        const formulaData = convertFilterCriteriaToFormula(
          enhancedFilterCriteria[fieldName],
        );

        const applyTo = axisRows.includes(fieldName)
          ? "Rows"
          : axisColumns.includes(fieldName)
            ? "Columns"
            : "Filters";
        const filter = {
          selectedFilters: selectionData.selectedFilters,
          exclude:
            typeof selectionData.exclude === "boolean"
              ? selectionData.exclude
              : false,
          applyTo,
          isForNamedSet:
            selectionData.selectedFilters.length <= 0
              ? false
              : selectionData.selectedFilters.some((sel) => sel.isNamedSet) ||
                filterSelections.isForNamedSet ||
                false,
          condition: conditionData ?? undefined,
          formula: formulaData ?? undefined,
        };
        setFilterSelections(filter);
      } else {
        const applyTo = axisRows.includes(fieldName)
          ? "Rows"
          : axisColumns.includes(fieldName)
            ? "Columns"
            : null;
        setFilterSelections({ ...defaultFilterSelections, applyTo });
      }
    }
  }, [queryBuilder.query]);
  useEffect(() => {
    if (filterSelections.applyTo && filterSelections.applyTo !== "Filters") {
      const selectedFilters = filterSelections.selectedFilters ?? [];
      const hasNamedSetFilters = selectedFilters.some((flr) => flr.isNamedSet);
      const hasDisplayFilters = selectedFilters.some((flr) => !flr.isNamedSet);
      const hasConditionOrFormula = Boolean(
        filterSelections?.condition || filterSelections?.formula,
      );

      if (activePanelTab === "namedSet") {
        setRowColumnFilterApplied(hasNamedSetFilters);
      } else if (activePanelTab === "displayFields") {
        setRowColumnFilterApplied(
          hasDisplayFilters ||
            selectedFilters.length == 0 ||
            hasConditionOrFormula,
        );
      } else {
        setRowColumnFilterApplied(false);
      }
    } else {
      setRowColumnFilterApplied(false);
    }
  }, [
    filterSelections.applyTo,
    filterSelections.selectedFilters,
    filterSelections.condition,
    filterSelections.formula,
    activePanelTab,
  ]);
  return (
    <div className={`item ${rowColumnFilterApplied ? "filter-applied" : ""}`}>
      <div
        id={treeData.uniquename}
        data-name={treeData.uniquename}
        className="child-node"
      >
        {/* {rowColumnFilterApplied && filterSelections.applyTo && (
          <span className="filter-icon-circle">
            <TooltipIcon
              icon={filterSelections.applyTo === "Rows" ? faR : faC}
              message={filterSelections.applyTo ?? ""}
            />
          </span>
        )} */}
        {rowColumnFilterApplied && filterSelections.applyTo && (
          <div className="filter-icon-circle">
            <TooltipWrapper
              content={filterSelections.applyTo ?? ""}
              position="top"
            >
              <span style={{ lineHeight: 1, color: "#fff", fontSize: 12 }}>
                {filterSelections.applyTo === "Rows" ? "R" : "C"}
              </span>
            </TooltipWrapper>
          </div>
        )}
        <span
          className="nav-label"
          onClick={() =>
            activePanelTab === "displayFields"
              ? !hasNamedSetFilters ? labelClickHandler() : null
              : filterClickHandler()
          }
        >
          {`${treeData.caption}${activePanelTab === "namedSet" ? " Set" : ""}`}
        </span>
        {treeData?.properties["map.enable"] === "Y" &&
          activePanelTab === "displayFields" && (
            <span className="map-icon">
              <TooltipIcon icon={faGlobe} message="" />
            </span>
          )}
        <span className="filter-icon" onClick={() => filterClickHandler()}>
          <TooltipIcon
            icon={
              treeData.uniquename &&
              queryBuilder?.query?.enhancedFilterCriteria[
                treeData.uniquename
              ] &&
              isFilterMatchForActivePanel
                ? faFilterCircleXmark
                : faFilterSolid
            }
            message=""
            position="left"
          />
        </span>
      </div>
    </div>
  );
}

interface TreeProps {
  treeData: TreeNode;
  expandedNodes: string[];
  activePanelTab: string;
  toggleNode: (id: string) => void;
  currentActiveTab: number;
}

interface TreeNodeItemProps {
  treeData: TreeNode;
  currentActiveTab: number;
  isExpanded: boolean;
  activePanelTab: string;
  callBack: () => void;
}

interface LevelItemProps {
  treeData: TreeNode;
  currentActiveTab: number;
  activePanelTab: string;
}
